import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useState, useEffect } from "react";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TextField } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import "./PaymentDetails.css";
import { isAmountGreater, PaymentDetail, PaymentDetailsProps } from "../../collectionUtils";

const PaymentDetails: React.FC<PaymentDetailsProps> = ({
  paymentDetail,
  parentIndex,
  isOpen,
  onToggle,
  onUpdate,
  onOpenModal,
  collectedAmount,
  totalAmount
}) => {
  const [amountDisplayValue, setAmountDisplayValue] = useState<string>(
    paymentDetail.amount?.toString() || "0"
  );

  const [chequeDate, setChequeDate] = useState<Dayjs | null>(
    paymentDetail.chequeDate ? dayjs(paymentDetail.chequeDate) : null
  );

  useEffect(() => {
    setAmountDisplayValue(paymentDetail.amount?.toString() || "0");
  }, [paymentDetail.amount]);

  useEffect(() => {
    setChequeDate(paymentDetail.chequeDate ? dayjs(paymentDetail.chequeDate) : null);
  }, [paymentDetail.chequeDate]);


  // Validation function for bank account number
const validateBankAccountNumber = (accountNo: string): string | null => {
  const trimmed = accountNo.trim();
  if (trimmed.length === 0) {
    return "Bank account number cannot be empty!";
  }
  if (trimmed.length < 9 || trimmed.length > 18) {
    return "Bank account number must be between 9 and 18 digits!";
  }
  if (/^0+$/.test(trimmed)) {
    return "Bank account number cannot be all zeros!";
  }
  // Optional: Reject repetitive digits
  if (/^(\d)\1+$/.test(trimmed)) {
    return "Bank account number cannot be all identical digits!";
  }
  return null;
};


  const validateIfscCode = (ifsc: string): string | null => {
  const trimmedIfsc = ifsc.trim().toUpperCase();
  if (!trimmedIfsc) return "IFSC code cannot be empty!";
  if (trimmedIfsc.length !== 11) return "IFSC code must be exactly 11 characters long!";
  const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
  if (!ifscRegex.test(trimmedIfsc)) {
    return "IFSC code must start with 4 letters (A–Z), followed by '0', then 6 alphanumeric characters (A–Z, 0–9)!";
  }
  return null;
};

const validateUpiTransactionId = (id: string): string | null => {
  let cleanedId = id.trim().replace(/[^A-Za-z0-9]/g, ''); // Remove non-alphanumerics
  if (!cleanedId) return "UPI Transaction ID cannot be empty!";
  if (cleanedId.length < 12 || cleanedId.length > 20) {
    return "UPI Transaction ID must be between 12 and 20 characters!";
  }
  if (!/^[A-Za-z0-9]+$/.test(cleanedId)) {
    return "UPI Transaction ID must contain only alphanumeric characters!";
  }
  return null;
};

const handleInputChange = (field: keyof PaymentDetail, value: string) => {
  const updatedDetail = { ...paymentDetail };

  if (field === "amount") {
    if (value.startsWith('-')) return;
    const regex = /^\d*(\.\d{0,2})?$/;
    if (!regex.test(value)) return;

    const newAmount = parseFloat(value) || 0;
    const otherPaymentsTotal = collectedAmount - (paymentDetail.amount || 0);
    const newCollectedAmount = otherPaymentsTotal + newAmount;

    // Use precise decimal comparison to fix floating point precision issues
    if (isAmountGreater(newCollectedAmount, totalAmount)) {
      onOpenModal("Paid amount cannot be greater than balance amount!");
      return;
    }

    setAmountDisplayValue(value);
    updatedDetail.amount = newAmount;
    onUpdate(updatedDetail, parentIndex);
  } else if (field === "ifscCode" && (paymentDetail.mode === "Cheque" || paymentDetail.mode === "NEFT")) {
    const upperCaseValue = value.trim().toUpperCase();
    if (upperCaseValue.length > 11) return
    updatedDetail.ifscCode = upperCaseValue;
    onUpdate(updatedDetail, parentIndex);
  } else if (field === "transactionId" && paymentDetail.mode === "UPI") {
    const cleanedValue = value.trim().replace(/[^A-Za-z0-9]/g, '');
    if (cleanedValue.length > 20) return;
    updatedDetail.transactionId = cleanedValue;
    onUpdate(updatedDetail, parentIndex);
  } else if (field === "bankAccountNo"){
    if (value.length > 18) return;
    updatedDetail.bankAccountNo = value;
    onUpdate(updatedDetail, parentIndex);
  } 
  else {
    updatedDetail[field] = value;
    onUpdate(updatedDetail, parentIndex);
  }
};

  const handleAmountBlur = () => {
    let value = parseFloat(amountDisplayValue);
    if (isNaN(value)) {
        value = 0;
    }
    // Format the display value to a clean string representation of the number
    const cleanValueStr = value.toString();
    setAmountDisplayValue(cleanValueStr);

    // Ensure the parent component has the final, clean numeric value
    if (paymentDetail.amount !== value) {
        const updatedDetail = { ...paymentDetail, amount: value };
        onUpdate(updatedDetail, parentIndex);
    }
  };

  const handleDateChange = (value: Dayjs | null) => {
    if (value) {
      setChequeDate(value);
      const updatedDetail = { ...paymentDetail };
      updatedDetail.chequeDate = value.format('YYYY-MM-DD');
      onUpdate(updatedDetail, parentIndex);
    }
  };

  const handleBlur = (field: keyof PaymentDetail) => {
  if (field === "amount") {
    let value = parseFloat(amountDisplayValue) || 0;
    const cleanValueStr = value.toString();
    setAmountDisplayValue(cleanValueStr);
    if (paymentDetail.amount !== value) {
      const updatedDetail = { ...paymentDetail, amount: value };
      onUpdate(updatedDetail, parentIndex);
    }
  } else if (field === "ifscCode" && (paymentDetail.mode === "Cheque" || paymentDetail.mode === "NEFT")) {
    const error = validateIfscCode(paymentDetail.ifscCode || "");
    if (error) onOpenModal(error);
  } else if (field === "transactionId" && paymentDetail.mode === "UPI") {
    const error = validateUpiTransactionId(paymentDetail.transactionId || "");
    if (error) onOpenModal(error);
  } else if (field === "bankAccountNo" && (paymentDetail.mode === "Cheque" || paymentDetail.mode === "NEFT")) {
      const error = validateBankAccountNumber(paymentDetail.bankAccountNo || "");
      if (error) onOpenModal(error);
    }
};

  // Determine the label text based on the payment method
  const getLabelText = (mode: string = "") => {
    switch (mode) {
      case "Cash":
        return "Enter the amount of cash collected";
      case "UPI":
        return "Enter the amount of UPI collected";
      case "Cheque":
        return "Enter the amount of cheque collected";
      case "NEFT":
        return "Enter the amount of NEFT collected";
      default:
        return "Enter the amount collected";
    }
  };

  return (
    <div>
      <div className="payment-details-label-wrapper">
        <span className="payment-details-section-label">
          {getLabelText(paymentDetail.mode)}
        </span>
      </div>
      <div className="payment-details-container">
        <div className="payment-details-header" onClick={onToggle}>
          <span className="payment-details-header-text">{paymentDetail.mode}</span>
          <ArrowDropDownIcon
            sx={{
              color: "#6E6969",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease"
            }}
          />
        </div>
        {isOpen && (
          <div className="payment-details-list">
            <div className="payment-details-item">
              {paymentDetail.mode === "Cash" ? (
                <div className="payment-details-detail full-width">
                  <span className="payment-details-label">
                    Collected Amount<span style={{ color: "#C44C36" }}>*</span>
                  </span>
                 <input
  type="text"
  inputMode="decimal"
  className="payment-details-input"
  value={amountDisplayValue}
  onChange={(e) => handleInputChange("amount", e.target.value)}
  onBlur={() => handleBlur("amount")}
/>
                </div>
              ) : paymentDetail.mode === "UPI" ? (
                <div className="payment-details-row">
                  <div className="payment-details-detail half-width">
                    <span className="payment-details-label">
                      Collected Amount<span style={{ color: "#C44C36" }}>*</span>
                    </span>
                   <input
  type="text"
  inputMode="decimal"
  className="payment-details-input"
  value={amountDisplayValue}
  onChange={(e) => handleInputChange("amount", e.target.value)}
  onBlur={() => handleBlur("amount")}
/>
                  </div>
                  <div className="payment-details-detail half-width">
                    <span className="payment-details-label">
                      Transaction ID<span style={{ color: "#C44C36" }}>*</span>
                    </span>
                   <input
  type="text"
  className="payment-details-input"
  value={paymentDetail.transactionId || ""}
  onChange={(e) => handleInputChange("transactionId", e.target.value)}
  onBlur={() => handleBlur("transactionId")}
/>
                  </div>
                </div>
              ) : paymentDetail.mode === "Cheque" ? (
                <>
                  <div className="payment-details-detail full-width">
                    <span className="payment-details-label">
                      Collected Amount<span style={{ color: "#C44C36" }}>*</span>
                    </span>
                  <input
  type="text"
  inputMode="decimal"
  className="payment-details-input"
  value={amountDisplayValue}
  onChange={(e) => handleInputChange("amount", e.target.value)}
  onBlur={() => handleBlur("amount")}
/>
                  </div>
                  <div className="payment-details-row">
                    <div className="payment-details-detail half-width">
                      <span className="payment-details-label">
                        Cheque Date<span style={{ color: "#C44C36" }}>*</span>
                      </span>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          inputFormat="DD-MM-YYYY"
                          value={chequeDate}
                          maxDate={dayjs()}
                          onChange={handleDateChange}
                          renderInput={(props) => (
                            <TextField
                              {...props}
                              inputProps={{
                                ...props.inputProps,
                                readOnly: true,
                              }}
                              size="small"
                              fullWidth
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>
                    <div className="payment-details-detail half-width">
                      <span className="payment-details-label">
                        Bank Name<span style={{ color: "#C44C36" }}>*</span>
                      </span>
                      <input
                        type="text"
                        className="payment-details-input"
                        value={paymentDetail.bankName || ""}
                        onChange={(e) => handleInputChange("bankName", e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="payment-details-row">
                    <div className="payment-details-detail half-width">
                      <span className="payment-details-label">
                        Bank A/c No.<span style={{ color: "#C44C36" }}>*</span>
                      </span>
                      <input
                        type="text"
                        className="payment-details-input"
                        value={paymentDetail.bankAccountNo || ""}
                        onChange={(e) => handleInputChange("bankAccountNo", e.target.value)}
                        onBlur={() => handleBlur("bankAccountNo")}
                      />
                    </div>
                    <div className="payment-details-detail half-width">
                      <span className="payment-details-label">
                        IFSC Code<span style={{ color: "#C44C36" }}>*</span>
                      </span>
                      <input
  type="text"
  className="payment-details-input"
  value={paymentDetail.ifscCode || ""}
  onChange={(e) => handleInputChange("ifscCode", e.target.value)}
  onBlur={() => handleBlur("ifscCode")}
/>
                    </div>
                  </div>
                </>
              ) : paymentDetail.mode === "NEFT" ? (
                <>
                  <div className="payment-details-detail full-width">
                    <span className="payment-details-label">
                      Collected Amount<span style={{ color: "#C44C36" }}>*</span>
                    </span>
                   <input
  type="text"
  inputMode="decimal"
  className="payment-details-input"
  value={amountDisplayValue}
  onChange={(e) => handleInputChange("amount", e.target.value)}
  onBlur={() => handleBlur("amount")}
/>
                  </div>
                  <div className="payment-details-row">
                    <div className="payment-details-detail half-width">
                      <span className="payment-details-label">
                        Payee Name<span style={{ color: "#C44C36" }}>*</span>
                      </span>
                      <input
                        type="text"
                        className="payment-details-input"
                        value={paymentDetail.payeeName || ""}
                        onChange={(e) => handleInputChange("payeeName", e.target.value)}
                      />
                    </div>
                    <div className="payment-details-detail half-width">
                      <span className="payment-details-label">
                        Bank Name<span style={{ color: "#C44C36" }}>*</span>
                      </span>
                      <input
                        type="text"
                        className="payment-details-input"
                        value={paymentDetail.bankName || ""}
                        onChange={(e) => handleInputChange("bankName", e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="payment-details-row">
                    <div className="payment-details-detail half-width">
                      <span className="payment-details-label">
                        Bank A/c No.<span style={{ color: "#C44C36" }}>*</span>
                      </span>
                      <input
                        type="text"
                        className="payment-details-input"
                        value={paymentDetail.bankAccountNo || ""}
                        onChange={(e) => handleInputChange("bankAccountNo", e.target.value)}
                        onBlur={() => handleBlur("bankAccountNo")}
                      />
                    </div>
                    <div className="payment-details-detail half-width">
                      <span className="payment-details-label">
                        IFSC Code<span style={{ color: "#C44C36" }}>*</span>
                      </span>
                      <input
  type="text"
  className="payment-details-input"
  value={paymentDetail.ifscCode || ""}
  onChange={(e) => handleInputChange("ifscCode", e.target.value)}
  onBlur={() => handleBlur("ifscCode")}
/>
                    </div>
                  </div>
                   <div className="payment-details-row">
                    <div className="payment-details-detail half-width">
                      <span className="payment-details-label">
                        Transaction ID<span style={{ color: "#C44C36" }}>*</span>
                      </span>
                      <input
  type="text"
  className="payment-details-input"
  value={paymentDetail.transactionId || ""}
  onChange={(e) => handleInputChange("transactionId", e.target.value)}
  onBlur={() => handleBlur("transactionId")}
/>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentDetails;